import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { eliminarNotificacion, listadoNotificaciones, updateNotification } from "../services/notificaciones.js";
import { useAuth } from "@conductor/context/AuthContext.jsx";
import useToast from "@shared/hooks/useToast.jsx";
import { useSocket } from "@conductor/hooks/useSocket";

const NotifyContext = createContext(null);

export const NotifyProvider = ({ children }) => {
    const [notificaciones, setNotificaciones] = useState([]);
    const [loading, setLoading] = useState(false);
    const { userData } = useAuth();
    const { newToast } = useToast();

    const fetchNotificaciones = useCallback(async () => {
        if (!userData?.id) {
            setNotificaciones([]);
            return [];
        }
        setLoading(true);
        try {
            const response = await listadoNotificaciones();
            const list = response?.estado > 0 ? (response.data ?? []) : [];
            setNotificaciones(list);
            return list;
        } catch (err) {
            setNotificaciones([]);
            return [];
        } finally {
            setLoading(false);
        }
    }, [userData?.id]);

    // Listener socket: nueva notificación en tiempo real
    useSocket([
        {
            event: 'notificacion',
            handler: ({ conductorId, notification }) => {
                if (conductorId !== userData?.id) return;
                setNotificaciones(prev => [notification, ...prev]);
                newToast(notification.title, 'info');
            },
        },
    ]);

    const countNotify = useMemo(
        () => notificaciones.filter(n => !n?.leido).length,
        [notificaciones]
    );

    const actualizarEstado = useCallback(async (id) => {
        try {
            const res = await updateNotification(id);
            if (!res || res.estado === 0) {
                newToast("No se pudo modificar la notificación", "error");
                return false;
            }
            setNotificaciones(prev => prev.map(n => (n.id === id ? { ...n, leido: true } : n)));
            return true;
        } catch (err) {
            console.error(err);
            newToast("Error al modificar la notificación", "error");
            return false;
        }
    }, [newToast]);

    const borrarNotificacion = useCallback(async (id) => {
        try {
            const res = await eliminarNotificacion(id);
            if (res?.estado) {
                setNotificaciones(prev => prev.filter(n => n.id !== id));
                newToast("Se eliminó con éxito");
                return true;
            }
            newToast("No se pudo eliminar la notificación", "error");
            return false;
        } catch (err) {
            console.error(err);
            newToast("Error al eliminar la notificación", "error");
            return false;
        }
    }, [newToast]);

    useEffect(() => {
        fetchNotificaciones();
    }, [fetchNotificaciones]);

    const value = useMemo(() => ({
        fetchNotificaciones,
        actualizarEstado,
        borrarNotificacion,
        notificaciones,
        countNotify,
        loading,
    }), [fetchNotificaciones, actualizarEstado, borrarNotificacion, notificaciones, countNotify, loading]);

    return (
        <NotifyContext.Provider value={value}>
            {children}
        </NotifyContext.Provider>
    );
};

export const useNotify = () => useContext(NotifyContext);
